import type { ReactNode } from 'react';
import type { ListingPagination } from '../../hooks/api/hook-builders/listing-hook-builder';
import type {
  Media,
  MediaHydrated,
} from '../../lib/flotiq-client/types/api-response';
import type { FileWithId, NewMediaObject } from '../../lib/store/newMediaStore';
import type { PageChangeFn } from '../Pagination/Pagination';
import type { ResultsPerPageChangeFn } from '../Pagination/ResultsPerPageDropdown';

import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { twMerge } from 'tailwind-merge';

import { EMPTY_ARRAY, EMPTY_OBJECT } from '../../lib/globals';
import { getTestProps } from '../../lib/helpers';

import { ListViewIcon, TilesViewIcon, WarningIcon } from '../../images/shapes';

import Button from '../Button/Button';
import Heading from '../Heading/Heading';
import Loader from '../Loader/Loader';
import Pagination from '../Pagination/Pagination';
import ResultsPerPageDropdown from '../Pagination/ResultsPerPageDropdown';
import Panel from '../Panel/Panel';

const RESULTS_PER_PAGE = [12, 24, 30, 42, 60];

export type RenderMediaGridDataFn = (
  media: any,
  isNew?: boolean,
  index?: number,
) => ReactNode;

export type MediaObject = Media | MediaHydrated | FileWithId | { id: string };

interface MediaGridProps {
  /**
   * Object with media data
   */
  data: any[];
  /**
   * Media render callback
   */
  renderMedia: RenderMediaGridDataFn;
  /**
   * Object with pagination data
   */
  pagination?: ListingPagination;
  /**
   * Currently displayed page number
   */
  page: number;
  /**
   * Callback handling changing page
   */
  handlePageChange: PageChangeFn;
  /**
   * Bool for displaying loading screen
   */
  isLoading?: boolean;
  /**
   * Information to display when there is no data
   */
  handleNoData?: ReactNode;
  /**
   * Media loading errors
   */
  errors?: any;
  /**
   * Node with filters inputs
   */
  filtersNode?: ReactNode;
  /**
   * Node with active filters
   */
  activeFilters?: any;
  /**
   * Bool for checking are filters applied
   */
  areFiltersApplied?: boolean;
  /**
   * Bar for handle selected media
   */
  selectedMediaActionBarNode?: ReactNode;
  /**
   * New uploaded media
   */
  newMedia?: NewMediaObject[];
  /**
   * Selected media
   */
  selectedMedia?: Record<string, { id: string }>;
  /**
   * If filters are packed in a panel
   */
  hasPanel?: boolean;
  /**
   * Text to show when data are empty after filtering
   */
  emptyFilterText?: string;
  /**
   * Additional grid container classes
   */
  additionalGridContainerClasses?: string;
  /**
   * Additional grid classes
   */
  additionalGridClasses?: string;
  /**
   *  Test id for MediaGrid
   */
  testId?: string;
  /**
   *  Results per page
   */
  resultsPerPage?: any;
  /**
   *  Set results per page
   */
  setResultsPerPage?: ResultsPerPageChangeFn;
  /**
   *  Grid view mode
   */
  gridView?: ReactNode;
}

const MediaGrid = ({
  data,
  pagination,
  page,
  handlePageChange,
  isLoading = false,
  handleNoData = '',
  errors = null,
  filtersNode = '',
  activeFilters = EMPTY_OBJECT,
  areFiltersApplied = false,
  selectedMediaActionBarNode = '',
  newMedia = EMPTY_ARRAY,
  selectedMedia = EMPTY_OBJECT,
  renderMedia,
  hasPanel = true,
  emptyFilterText = '',
  resultsPerPage,
  setResultsPerPage,
  gridView,
  additionalGridContainerClasses = '',
  additionalGridClasses = '',
  testId = '',
}: MediaGridProps) => {
  const { t } = useTranslation();
  const [firstLoading, setFirstLoading] = useState(true);
  const [isGridView, setIsGridView] = useState(false);

  const hideSelect =
    (firstLoading || (data.length === 0 && !areFiltersApplied)) &&
    newMedia.length === 0;

  useEffect(() => {
    if (!isLoading) setFirstLoading(false);
  }, [isLoading]);

  const handleEmptyResult = useMemo(() => {
    if (data.length > 0 && !errors && !isLoading) return '';
    if (isLoading) {
      return (
        <Loader
          size={'big'}
          type={'spinner-grid'}
          {...getTestProps(testId, 'media-loading', 'testId')}
        />
      );
    }
    if (areFiltersApplied || errors)
      return (
        <Heading level={2} additionalClasses="dark:text-slate-200">
          <div
            className={'inline-flex items-center'}
            {...getTestProps(testId, 'media-error-message-container')}
          >
            <WarningIcon className="text-red h-10 mr-3" />
            {errors ? t('Media.OnErrorMessage') : emptyFilterText}
          </div>
        </Heading>
      );
    return handleNoData;
  }, [
    errors,
    handleNoData,
    areFiltersApplied,
    isLoading,
    data,
    emptyFilterText,
    testId,
    t,
  ]);

  const filtersElement = useMemo(() => {
    if (filtersNode) {
      return hasPanel ? (
        <Panel
          title={
            <div>
              {Object.keys(activeFilters).length > 0 ? (
                <>
                  {t('Media.ActiveFiltersHeader')}
                  {': '}
                  <div className="block sm:inline-block">
                    {Object.keys(activeFilters).map((key, index) => (
                      <span key={key} className="font-normal">
                        {activeFilters[key]}
                        {index !== Object.keys(activeFilters).length - 1 &&
                          ', '}
                      </span>
                    ))}
                  </div>
                </>
              ) : (
                t('Media.FiltersHeader')
              )}
            </div>
          }
          additionalContainerClasses="mt-1 py-3! px-4! lg:py-5! z-11 select-none"
          additionalChildrenContainerClasses={
            'absolute w-full left-0  bg-white dark:bg-slate-950 px-4 pb-3 lg:pb-5 rounded-lg shadow-md'
          }
          additionalIconClasses="top-1/2! -mt-1"
          testId={testId}
        >
          {filtersNode}
        </Panel>
      ) : (
        <div className="mt-2 mb-2">{filtersNode}</div>
      );
    }
  }, [filtersNode, hasPanel, t, activeFilters, testId]);

  const mediaLayout = useMemo(() => {
    if (isGridView) return gridView;
    return (
      <div className="relative md:flex-1 md:min-h-0">
        {isLoading && (
          <div className="absolute inset-0 w-full h-full z-10">
            <div
              className="w-full h-full bg-white opacity-80
              bg-blend-multiply dark:bg-slate-900/90 rounded-lg"
            />
            <div className="absolute top-12 md:top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2">
              <Loader
                size={'veryBig'}
                type={'spinner-grid'}
                {...getTestProps(testId, 'media-loading', 'testId')}
              />
            </div>
          </div>
        )}
        <div
          className={twMerge(
            'w-full h-full md:overflow-auto scrollbar-sm',
            hasPanel && 'p-2 sm:px-5 sm:pt-2 sm:pb-8',
            additionalGridContainerClasses,
          )}
        >
          <div
            className={twMerge(
              'grid gap-4 sm:gap-6 grid-cols-[repeat(auto-fill,minmax(12rem,1fr))]',
              'sm:grid-cols-[repeat(auto-fill,minmax(14.9rem,1fr))] relative',
              additionalGridClasses,
            )}
            {...getTestProps(testId, 'media-grid')}
          >
            {newMedia.map((media, index) => renderMedia(media, true, index))}
            {data.map((media, index) => renderMedia(media, false, index))}
          </div>
        </div>
      </div>
    );
  }, [
    isGridView,
    gridView,
    isLoading,
    testId,
    hasPanel,
    additionalGridContainerClasses,
    additionalGridClasses,
    newMedia,
    data,
    renderMedia,
  ]);

  return (
    <div
      className="flex flex-col h-full md:min-h-0"
      {...getTestProps(testId, 'grid-container')}
    >
      {filtersElement}
      <div className="flex flex-col w-full h-full relative md:min-h-0">
        <div
          className={twMerge(
            'flex items-start sm:items-center pb-4 md:py-6 md:shrink-0',
            selectedMedia && Object.values(selectedMedia).length > 0
              ? 'flex-col md:flex-row gap-4'
              : 'flex-row',
            selectedMediaActionBarNode ? 'justify-between' : 'justify-center',
          )}
        >
          {!hideSelect && selectedMediaActionBarNode}
          {!errors && data.length > 0 && (
            <div
              className="flex flex-wrap sm:flex-nowrap items-center justify-end gap-2 sm:gap-4 mt-2 sm:mt-0
              space-x-3"
            >
              <Pagination
                numOfPages={pagination?.total_pages || 1}
                page={page}
                onPageChange={handlePageChange}
                additionalClasses="whitespace-nowrap p-0!"
                {...getTestProps(testId, 'pagination', 'testId')}
              />
              {gridView && (
                <div className="flex gap-2">
                  <Button
                    iconImage={
                      <ListViewIcon
                        className={twMerge(
                          'hover:opacity-50 w-5',
                          !isGridView ? 'text-slate-300' : 'text-blue',
                        )}
                      />
                    }
                    buttonColor="borderless"
                    buttonSize="lg"
                    onClick={() => setIsGridView(true)}
                    additionalClasses="p-2"
                    {...getTestProps(testId, 'grid-view', 'testId')}
                  />
                  <Button
                    iconImage={
                      <TilesViewIcon
                        className={twMerge(
                          'hover:opacity-50 w-5',
                          isGridView ? 'text-slate-300' : 'text-blue',
                        )}
                      />
                    }
                    buttonColor="borderless"
                    buttonSize="lg"
                    onClick={() => setIsGridView(false)}
                    additionalClasses="p-2"
                    {...getTestProps(testId, 'tiles-view', 'testId')}
                  />
                </div>
              )}
              {setResultsPerPage && (
                <ResultsPerPageDropdown
                  value={resultsPerPage}
                  setResultsPerPage={setResultsPerPage}
                  additionalButtonClasses="pr-8! 2xl:py-1!"
                  additionalClasses="p-0! text-sm md:text-base"
                  results={RESULTS_PER_PAGE}
                  label={t('Global.ResultsPerPage')}
                />
              )}
            </div>
          )}
        </div>
        {(errors || (isLoading && firstLoading) || data.length === 0) &&
        newMedia.length === 0 ? (
          <div className="m-auto">{handleEmptyResult}</div>
        ) : (
          mediaLayout
        )}
      </div>
    </div>
  );
};
export default MediaGrid;
